import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CloudProviderContext, getAccountCloudType } from "../../../CloudProviderContext";
import { useAccounts } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { listAllCatalogFrameworks } from "../../../helpers/auditHub_helper";
import { AWS_ACCOUNT_NUMBER } from "./constants";
import { frameworkLabel } from "./utils";

const CATALOG_CONCURRENCY = 4;

const addonIdOf = (fw) => {
  const id = Number(fw?.complianceAddonId);
  return Number.isInteger(id) ? id : null;
};

/**
 * Each account's catalog is the eligibility POST /audits/{id}/accounts enforces. The catalogs of the
 * provider's accounts (plus `accountNumbers`) are read a few at a time and inverted into the frameworks
 * at least one account can be assessed on. `supports(accountNumber, addonId)` is null until that
 * account's catalog has answered.
 */
const useFrameworkCatalogs = ({ enabled, accountNumbers = [] }) => {
  const accounts = useAccounts();
  const { selectedProvider } = useContext(CloudProviderContext);
  const [catalogs, setCatalogs] = useState({});
  const [retryKey, setRetryKey] = useState(0);
  const catalogRef = useRef({});
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const providerNumbers = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts
      .filter((account) => {
        if (account.accountNumber?.includes("INIT00000001")) return false;
        if (account.accountNumber?.includes("TEST")) return false;
        if (!AWS_ACCOUNT_NUMBER.test(account.accountNumber || "")) return false;
        return getAccountCloudType(account) === selectedProvider;
      })
      .map((account) => account.accountNumber);
  }, [accounts, selectedProvider]);

  const extraKey = accountNumbers.filter(Boolean).join(",");
  const numbersKey = useMemo(
    () => [...new Set([...providerNumbers, ...extraKey.split(",")].filter(Boolean))].sort().join(","),
    [providerNumbers, extraKey]
  );

  useEffect(() => {
    if (!enabled) return;
    const pending = numbersKey.split(",").filter((number) => number && !catalogRef.current[number]);
    if (!pending.length) return;
    pending.forEach((number) => {
      catalogRef.current[number] = { status: "loading", ids: [], items: [] };
    });
    setCatalogs({ ...catalogRef.current });
    let index = 0;
    const loadNext = async () => {
      while (index < pending.length) {
        const number = pending[index];
        index += 1;
        try {
          const items = await listAllCatalogFrameworks({ accountNumber: number });
          const valid = items.filter((fw) => addonIdOf(fw) != null);
          catalogRef.current[number] = { status: "succeeded", ids: valid.map(addonIdOf), items: valid };
        } catch (_error) {
          catalogRef.current[number] = { status: "failed", ids: [], items: [] };
        }
        if (mountedRef.current) setCatalogs({ ...catalogRef.current });
      }
    };
    for (let i = 0; i < Math.min(CATALOG_CONCURRENCY, pending.length); i += 1) loadNext();
  }, [enabled, numbersKey, retryKey]);

  const retry = useCallback(() => {
    Object.keys(catalogRef.current).forEach((number) => {
      if (catalogRef.current[number]?.status === "failed") delete catalogRef.current[number];
    });
    setRetryKey((n) => n + 1);
  }, []);

  /** Frameworks at least one provider account can be assessed on, with how many can. */
  const options = useMemo(() => {
    const byId = new Map();
    providerNumbers.forEach((number) => {
      const catalog = catalogs[number];
      if (catalog?.status !== "succeeded") return;
      catalog.items.forEach((fw) => {
        const id = addonIdOf(fw);
        const entry = byId.get(id) || { ...fw, complianceAddonId: id, eligibleAccounts: 0 };
        entry.eligibleAccounts += 1;
        byId.set(id, entry);
      });
    });
    return [...byId.values()].sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
  }, [providerNumbers, catalogs]);

  const supports = useCallback(
    (accountNumber, addonId) => {
      const catalog = catalogs[accountNumber];
      if (addonId == null || catalog?.status !== "succeeded") return null;
      return catalog.ids.includes(Number(addonId));
    },
    [catalogs]
  );

  const statuses = numbersKey.split(",").filter(Boolean).map((number) => catalogs[number]?.status);
  const loading = enabled && statuses.some((status) => !status || status === "loading");
  const failed = statuses.filter((status) => status === "failed").length;

  return { catalogs, options, supports, loading, failed, retry };
};

export default useFrameworkCatalogs;
